import {
  Async,
  F1,
  RemoveNull,
  RemoveUndefined,
  SMap,
  TMap,
  toAsyncError,
  toAsyncFetched,
  TypedValue,
} from '@client/new/helpers/types';
import { useCollectionsModel } from '../../collections/collections.model';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { SubscriptionType } from '../../collection/domain';
import { LiteCollection } from '../../collections/collections.domain';
import {
  BundlePricing,
  BundleStatus,
  CreateCouponMutation,
  GetBundlesByPricingQuery,
  GetCouponsByInfluencerQuery,
  GetSubTiresQuery,
  useCreateCouponMutation,
  useDeactivateCouponMutation,
  useGetBundlesByPricingQuery,
  useGetCouponsByInfluencerQuery,
  useGetOtpPriceQuery,
  useGetSubTiresQuery,
} from 'generated/graphql';
import { useAsyncResult } from '@client/new/helpers/gql';
import {
  CreatorType,
  mapSubscriptionTierPrices,
  SubscriptionTire,
  toCreatorType,
  toDiscounts,
} from './Discounts.mappers';
import { useEffect, useState } from 'react';
import { toSubscriptionPurchasePage } from '@client/helpers/routes';
import { useInfluencer } from '@client/new/common/LiteUser.mapper';
import {
  isOtpCreator,
  isSubscriptionCreator,
} from '@client/new/common/offering.helpers';
import { Influencer } from '@playbook-technologies/common-enums';

export type DiscountProduct =
  | TypedValue<'subscription', { periods: SubscriptionType[] }>
  | TypedValue<'otp', { otpId: string; name: string }>
  | TypedValue<'otp-bundle', { bundleId: string; name: string }>;

export type Discount = {
  detailURL: string; // todo: move to view
  name: string;
  code: string;
  url?: string;
  discountId: number;
  providerCouponId: string;
  createdAt: number;
  product: DiscountProduct;

  amount: { type: 'fixed' | 'percent'; value: number };
  expiresAt: number | null;
  redemptionsLimit: number | null;
  redemptions: number;
  status: 'Active' | 'Inactive' | 'Expired' | 'Limit reached';
  isProductPublished: boolean;
  isReferralCampaign?: boolean;
  compatibleWithFreeTrial: boolean;
};
export type SubscriptionPrice = {
  period: SubscriptionType;
  providerPriceId: string;
  price: number;
};

export type CreateCouponPayload = Pick<
  RemoveNull<CreateCouponMutation['createCoupon']>,
  'code' | 'discountType' | 'productType' | 'influencerId' | 'name'
> &
  Pick<
    RemoveUndefined<RemoveNull<CreateCouponMutation['createCoupon']>>,
    | 'subscriptionProviderPriceId'
    | 'paidProductIds'
    | 'percentOff'
    | 'discountAmount'
    | 'expiresAt'
    | 'maxRedemptions'
    | 'compatibleWithFreeTrial'
  >;

export type OtpBundle = {
  id: number;
  title: string;
  status: BundleStatus;
  oneTimeProduct?: {
    id: number;
    price: number;
    playbookFee: number;
    providerPriceId: string;
    providerProductId: string;
  } | null;
  checkoutPage?: {
    url: string;
  } | null;
};

export type DiscountsModel = {
  otps: LiteCollection[];
  otpBundles: OtpBundle[];
  discounts: Array<Discount>;
  subscriptionMap: Partial<TMap<SubscriptionType, SubscriptionPrice>>;
  hasOtpProducts: boolean;
  hasSubscriptionProducts: boolean;
  createDiscount: (v: CreateCouponPayload) => Promise<Discount>;
  deactivateDiscount: (discountId: number) => Promise<unknown>;
  creatorType: CreatorType;
  isCreatorLive: boolean;
  isCouponWithFreeTrialEnabled: boolean;
} & OTPModel;

export const useDiscountsModel = (): Async<DiscountsModel> => {
  const creatorId = useInfluencerId();
  const creator = useInfluencer(creatorId);
  const otpModel = useOTPModel();
  const otpsRes = useCollectionsModel({
    type: 'otp',
    influencerId: creatorId + '',
  });
  const otpBundlesRes = useOtpBundles(creatorId);

  const [createDiscountMutation] = useCreateCouponMutation();
  const [deactivateCouponMutation] = useDeactivateCouponMutation();
  const [discounts, setDiscounts] = useState<Discount[]>([]);

  const createDiscount = async (
    variables: CreateCouponPayload,
  ): Promise<Discount> => {
    if (asyncDiscounts.data.type !== 'fetched')
      return Promise.reject('data not fetched yet');
    variables.influencerId = creatorId;
    return createDiscountMutation({ variables }).then(async (v) => {
      const newDiscountID = v.data?.createCoupon.id;
      const data = await asyncDiscounts.refetch();
      const nextDiscounts = getDiscounts(data) || [];
      const d = nextDiscounts.find(
        (d) => `${d.discountId}` === `${newDiscountID}`,
      );
      if (!d) return Promise.reject('discount not created');
      return d;
    });
  };

  const deactivateDiscount = async (discountId: number) => {
    if (asyncDiscounts.data.type !== 'fetched') return;
    deactivateCouponMutation({ variables: { id: discountId } }).then(
      asyncDiscounts.refetch,
    );
  };

  const creatorType: CreatorType =
    creator.type === 'fetched' ? toCreatorType(creator.value) : 'legacy';

  const prices = usePrices({ creatorId });

  const asyncDiscounts = useDiscounts(creatorId);
  const getDiscounts = (data: Async<Coupons>) => {
    if (
      data.type !== 'fetched' ||
      otpsRes.type !== 'fetched' ||
      prices.type !== 'fetched' ||
      creator.type !== 'fetched' ||
      otpBundlesRes.type !== 'fetched'
    )
      return null;

    const discounts = toDiscounts({
      subscriptions: prices.value,
      subsCheckoutURL: toSubscriptionPurchasePage(creator.value),
      otps: otpsRes.value.collections.published,
      otpBundles: otpBundlesRes.value.bundle,
    })(data.value);
    return discounts;
  };

  useEffect(() => {
    const nextDiscounts = getDiscounts(asyncDiscounts.data);
    if (nextDiscounts) setDiscounts(nextDiscounts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncDiscounts.data.type, creator.type, otpsRes.type, prices.type]);

  try {
    if (asyncDiscounts.data.type !== 'fetched') return asyncDiscounts.data;
    if (otpsRes.type !== 'fetched') return otpsRes;
    if (prices.type !== 'fetched') return prices;
    if (creator.type !== 'fetched') return creator;
    if (otpBundlesRes.type !== 'fetched') return otpBundlesRes;
    const { collections } = otpsRes.value;

    const isCreatorLive = creator.value.mode === Influencer.Mode.Live.value;

    const isCouponWithFreeTrialEnabled =
      creator.value.customizations?.isCouponWithFreeTrialEnabled === true;

    const hasOtpProducts = isOtpCreator(creator.value.offering);
    const hasSubscriptionProducts = isSubscriptionCreator(
      creator.value.offering,
    );

    return {
      type: 'fetched',
      value: {
        creatorType,
        isCreatorLive,
        subscriptionMap: prices.value,
        otps: collections.published,
        otpBundles: otpBundlesRes.value.bundle,
        hasOtpProducts,
        hasSubscriptionProducts,
        isCouponWithFreeTrialEnabled,
        discounts,
        createDiscount,
        deactivateDiscount,
        ...otpModel,
      },
    };
  } catch (err) {
    return { type: 'fetch-error', value: `Cannot read model: ${err}` };
  }
};

type OTPModel = {
  otpPrices: SMap<Async<number>>;
  fetchOTPPrice: F1<number, Promise<Async<number>>>;
  setOtpPrice: (otpId: string, price: number) => void;
};
const useOTPModel = (): OTPModel => {
  const [otpPrices, setOtpPrices] = useState<OTPModel['otpPrices']>({});
  const getPrice = useGetOtpPriceQuery({ fetchPolicy: 'standby' });

  const fetchOTPPrice = async (otpId: number): Promise<Async<number>> => {
    const v = otpPrices[otpId] || { type: 'not-fetched' };

    if (v.type === 'fetching' || v.type === 'fetched') return v;

    setOtpPrices((prev) => ({ ...prev, [otpId]: { type: 'fetching' } }));
    try {
      const res = await getPrice.refetch({ collectionId: otpId });
      const price = res.data.collection.exclusiveProgramMetadata?.price;
      if (!price) throw new Error('Cannot fetch price for selected OTP');
      const value = toAsyncFetched(price);
      setOtpPrices((prev) => ({ ...prev, [otpId]: value }));
      return value;
    } catch (err) {
      const value = toAsyncError((err as Error)?.message || '');
      setOtpPrices((prev) => ({ ...prev, [otpId]: value }));
      return value;
    }
  };

  const setOtpPrice = (otpId: string, price: number) => {
    setOtpPrices((prev) => ({ ...prev, [otpId]: toAsyncFetched(price) }));
  };
  return {
    otpPrices,
    fetchOTPPrice,
    setOtpPrice,
  };
};

const usePrices = (p: {
  creatorId: number;
}): Async<DiscountsModel['subscriptionMap']> => {
  const q = useGetSubTiresQuery({ variables: { id: p.creatorId } });

  const res = useAsyncResult<GetSubTiresQuery>(q);
  if (res.type === 'fetched') {
    const { subscriptionTierByInfluencer } = res.value;

    if (
      !subscriptionTierByInfluencer ||
      subscriptionTierByInfluencer.length === 0 ||
      !subscriptionTierByInfluencer[0]
    )
      return toAsyncFetched({});
    const prices = mapSubscriptionTierPrices(
      subscriptionTierByInfluencer[0] as SubscriptionTire,
    );

    const pricesMap = prices.reduce(
      (acc, subPrice) => ({
        ...acc,
        [subPrice.period]: subPrice,
      }),
      {} as DiscountsModel['subscriptionMap'],
    );
    return toAsyncFetched(pricesMap);
  }
  return res;
};

type Coupons = GetCouponsByInfluencerQuery['getCouponsByInfluencer'];
const useDiscounts = (influencerId: number) => {
  const q = useGetCouponsByInfluencerQuery({
    variables: { id: influencerId },
  });
  const [data, setData] = useState<Async<Coupons>>({
    type: 'not-fetched',
  });
  const res = useAsyncResult<GetCouponsByInfluencerQuery>(q);
  useEffect(() => {
    if (res.type !== 'fetched') setData(res);
    else setData(toAsyncFetched(res.value.getCouponsByInfluencer));
  }, [res]);

  const refetch = (): Promise<Async<Coupons>> => {
    setData({ type: 'fetching' });
    return q
      .refetch()
      .then((v) => {
        const d: Async<Coupons> = toAsyncFetched(v.data.getCouponsByInfluencer);
        setData(d);
        return d;
      })
      .catch((err) => {
        const d: Async<Coupons> = toAsyncError(err);
        setData(d);
        return d;
      });
  };

  return {
    refetch,
    data,
  };
};

const useOtpBundles = (creatorId: number) => {
  const q = useGetBundlesByPricingQuery({
    variables: {
      creatorId,
      bundlePricing: BundlePricing.Otp,
    },
  });

  return useAsyncResult<GetBundlesByPricingQuery>(q);
};
